import React, { FunctionComponent } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import { View } from 'react-native'

import { addMinutes, differenceInHours, isPast, parseISO } from 'date-fns'
import { formatInTimeZone } from 'date-fns-tz'
import styled, { useTheme } from 'styled-components/native'

import { getProgramDisplayLabel } from '@lyrahealth-inc/shared-app-logic/src/features/programConfigs/programHelpers'
import {
  DBT_TEENS_SESSION_TYPE_LABELS,
  LTSessionTypes,
} from '@lyrahealth-inc/shared-app-logic/src/features/sessions/constants'
import { SessionTypeText } from '@lyrahealth-inc/shared-app-logic/src/features/sessions/messages'
import { isIntakeSession } from '@lyrahealth-inc/shared-app-logic/src/features/sessions/utils'
import { type PatientUpcomingAppointment } from '@lyrahealth-inc/shared-app-logic/src/models/appointment/Appointment'
import { ProgramNames } from '@lyrahealth-inc/shared-app-logic/src/models/programs/Programs'
import { MEETING_FORMATS } from '@lyrahealth-inc/shared-app-logic/src/models/session/Session'

import { BodyText, Size as BodyTextSize } from '../../atoms/bodyText/BodyText'
import { Divider } from '../../atoms/divider/Divider'
import { Avatar, avatarAltTextProvider } from '../../atoms/icons/Avatar'
import { BookSessionsIcon } from '../../atoms/icons/BookSessionsIcon'
import { loadIconComponent } from '../../atoms/icons/loadIconComponent'
import { LoadingIndicator } from '../../atoms/icons/LoadingIndicator'
import { getProgramIconName } from '../../atoms/icons/programIcons'
import { Link } from '../../atoms/link/Link'
import { Title, Size as TitleSize } from '../../atoms/title/Title'
import { CalendarCardActionMenu } from '../../molecules/calendarCard/CalendarCardActionMenu'
import { tID } from '../../utils/utils'

export type UpcomingSessionsModuleProps = {
  appointments: PatientUpcomingAppointment[]
  isLoading: boolean
  authProviderId: string
  displayTimeZone: string
  subtitle?: string
  onViewAllPress: () => void
  onSchedulePress?: () => void
  onReschedulePress: (appointment: PatientUpcomingAppointment) => void
  onCancelPress: (appointment: PatientUpcomingAppointment) => void
}

const SessionsCard = styled.View(({ theme: { colors, spacing } }) => ({
  backgroundColor: colors.backgroundPrimary,
  borderRadius: spacing['20px'],
  borderWidth: 1,
  borderColor: colors.cardOutlinePrimary,
  overflow: 'hidden',
}))

const ModuleHeader = styled.View(({ theme: { colors, spacing } }) => ({
  backgroundColor: colors.backgroundSecondary,
  flexDirection: 'row',
  alignItems: 'center',
  justifyContent: 'space-between',
  paddingVertical: spacing['16px'],
  paddingHorizontal: spacing['24px'],
}))

const ModuleHeaderText = styled.View(({ theme: { spacing } }) => ({
  flex: 1,
  gap: spacing['4px'],
}))

const SessionRow = styled.View(({ theme: { spacing } }) => ({
  flexDirection: 'row',
  alignItems: 'center',
  paddingVertical: spacing['16px'],
  paddingHorizontal: spacing['16px'],
}))

const DateBadge = styled.View<{ active?: boolean }>(({ theme: { colors, spacing }, active }) => ({
  width: 56,
  height: 60,
  alignItems: 'center',
  justifyContent: 'center',
  borderWidth: 1,
  borderColor: colors.cardOutlinePrimary,
  borderRadius: spacing['8px'],
  marginRight: spacing['16px'],
  flexShrink: 0,
  ...(active && { backgroundColor: colors.backgroundSecondary }),
}))

const SessionContent = styled.View(({ theme: { breakpoints } }) => ({
  flex: 1,
  ...(!breakpoints.isMobileSized && {
    flexDirection: 'row',
    alignItems: 'center',
  }),
}))

const SessionInfo = styled.View({
  flex: 1,
})

const ConcurrentCareDetails = styled.View(({ theme: { breakpoints, spacing } }) => ({
  flexDirection: 'row',
  alignItems: 'center',
  flexWrap: 'wrap',
  columnGap: breakpoints.isMobileSized ? spacing['8px'] : spacing['48px'],
  rowGap: spacing['4px'],
  marginTop: breakpoints.isMobileSized ? spacing['4px'] : 0,
  marginLeft: breakpoints.isMobileSized ? 0 : spacing['24px'],
  flexShrink: 1,
}))

const ProviderDetails = styled.View(({ theme: { spacing } }) => ({
  flexDirection: 'row',
  alignItems: 'center',
  gap: spacing['8px'],
}))

const ProgramDetails = styled.View(({ theme: { spacing } }) => ({
  flexDirection: 'row',
  alignItems: 'center',
  gap: spacing['8px'],
  marginRight: spacing['8px'],
}))

const ActionMenuContainer = styled.View(({ theme: { spacing } }) => ({
  flexShrink: 0,
  marginLeft: spacing['16px'],
  marginRight: -8,
}))

const EmptyStateRow = styled.View(({ theme: { spacing } }) => ({
  flexDirection: 'row',
  alignItems: 'center',
  gap: spacing['12px'],
  padding: spacing['24px'],
}))

const EmptyIconContainer = styled.View(({ theme: { colors } }) => ({
  width: 40,
  height: 40,
  borderRadius: 20,
  backgroundColor: colors.borderDefault,
  alignItems: 'center',
  justifyContent: 'center',
}))

const EmptyStateAction = styled.View({
  marginLeft: 'auto',
})

const LoadingContainer = styled.View(({ theme: { spacing } }) => ({
  alignItems: 'center',
  padding: spacing['24px'],
}))

const meetingFormatAliases: Partial<Record<MEETING_FORMATS, MEETING_FORMATS>> = {
  [MEETING_FORMATS.IN_PERSON_UNDERSCORE]: MEETING_FORMATS.IN_PERSON,
  [MEETING_FORMATS.CALL]: MEETING_FORMATS.PHONE,
}

const formatMeetingFormat = (meetingFormat: MEETING_FORMATS): string =>
  SessionTypeText[meetingFormatAliases[meetingFormat] ?? meetingFormat] ?? meetingFormat

const formatTimeRange = (start: Date, duration: number, timeZone: string): string => {
  const end = addMinutes(start, duration)
  const startAmPm = formatInTimeZone(start, timeZone, 'a')
  const endAmPm = formatInTimeZone(end, timeZone, 'a')
  if (startAmPm !== endAmPm) {
    return `${formatInTimeZone(start, timeZone, 'h:mm a')}-${formatInTimeZone(end, timeZone, 'h:mm a')}`
  }
  return `${formatInTimeZone(start, timeZone, 'h:mm')}-${formatInTimeZone(end, timeZone, 'h:mm')} ${endAmPm}`
}

export const UpcomingSessionsModule: FunctionComponent<UpcomingSessionsModuleProps> = ({
  appointments,
  isLoading,
  authProviderId,
  displayTimeZone,
  subtitle,
  onViewAllPress,
  onSchedulePress,
  onReschedulePress,
  onCancelPress,
}) => {
  const { colors } = useTheme()
  const intl = useIntl()
  const { formatMessage } = intl

  const renderSessionRow = (appointment: PatientUpcomingAppointment, index: number) => {
    const isOwnAppointment = appointment.provider.id === authProviderId
    const primaryColor = isOwnAppointment ? colors.textPrimary : colors.textInactive
    const secondaryColor = isOwnAppointment ? colors.textSecondary : colors.textInactive
    const appointmentStart = parseISO(appointment.startDateTime)
    const monthLabel = intl.formatDate(appointmentStart, { month: 'short', timeZone: displayTimeZone })
    const dayLabel = intl.formatDate(appointmentStart, { day: 'numeric', timeZone: displayTimeZone })
    const timeRange = formatTimeRange(appointmentStart, appointment.duration, displayTimeZone)
    const formatLabel = formatMeetingFormat(appointment.meetingFormat)
    const programLabel = getProgramDisplayLabel(intl, appointment.programName) || appointment.programName
    const programIconName = getProgramIconName(appointment.programName)
    const ProgramIcon = programIconName ? loadIconComponent(programIconName) : undefined

    const dbtTeensSessionType =
      appointment.sessionNumber === 1 ? LTSessionTypes.DBT_TEENS_INTAKE_SESSION : appointment.sessionType
    const dbtTeensSessionTypeLabel =
      appointment.programName === ProgramNames.DialecticalBehaviorTherapyTeens
        ? ((dbtTeensSessionType && DBT_TEENS_SESSION_TYPE_LABELS[dbtTeensSessionType]) ??
          DBT_TEENS_SESSION_TYPE_LABELS[LTSessionTypes.DBT_TEENS_SESSION])
        : undefined
    const appointmentTitle =
      dbtTeensSessionTypeLabel ??
      (isIntakeSession(appointment)
        ? formatMessage({ defaultMessage: 'Intake session', description: 'Title for an intake therapy session' })
        : formatMessage({ defaultMessage: 'Follow-up session', description: 'Title for a follow-up therapy session' }))

    const isTooLateToReschedule =
      !isPast(appointmentStart) &&
      appointment.appointmentStatus !== 'canceled' &&
      differenceInHours(appointmentStart, new Date()) < 24

    return (
      <View key={appointment.appointmentId}>
        {index > 0 && <Divider />}
        <SessionRow testID={tID(`UpcomingSessionsModule-row-${index}`)}>
          <DateBadge active={isOwnAppointment}>
            <BodyText text={monthLabel} size={BodyTextSize.SMALL} color={primaryColor} medium />
            <Title text={dayLabel} size={TitleSize.MEDIUM} color={primaryColor} medium />
          </DateBadge>
          <SessionContent>
            <SessionInfo>
              <BodyText text={appointmentTitle} size={BodyTextSize.DEFAULT} color={primaryColor} medium />
              <BodyText
                text={formatMessage(
                  { defaultMessage: '{timeRange} · {formatLabel}', description: 'Time range and meeting format label' },
                  { timeRange, formatLabel },
                )}
                size={BodyTextSize.SMALL}
                color={secondaryColor}
              />
            </SessionInfo>
            {!isOwnAppointment && (
              <ConcurrentCareDetails>
                <ProviderDetails>
                  <Avatar
                    details={{ src: appointment.provider.imageUrl, displayName: appointment.provider.displayName }}
                    size={24}
                    accessibilityLabel={formatMessage(avatarAltTextProvider, {
                      name: appointment.provider.displayName,
                    })}
                  />
                  <BodyText
                    text={appointment.provider.displayName}
                    size={BodyTextSize.SMALL}
                    color={colors.textSecondary}
                  />
                </ProviderDetails>
                <ProgramDetails testID={tID(`UpcomingSessionsModule-program-${appointment.programName}`)}>
                  {ProgramIcon && <ProgramIcon size={24} fillColor={colors.iconDefault} />}
                  <BodyText text={programLabel} size={BodyTextSize.SMALL} color={colors.textSecondary} />
                </ProgramDetails>
              </ConcurrentCareDetails>
            )}
            {isOwnAppointment && appointment.appointmentStatus !== 'canceled' && (
              <ActionMenuContainer>
                <CalendarCardActionMenu
                  handleReschedulePress={onReschedulePress}
                  handleCancelPress={onCancelPress}
                  isTooLateToReschedule={isTooLateToReschedule}
                  session={appointment}
                />
              </ActionMenuContainer>
            )}
          </SessionContent>
        </SessionRow>
      </View>
    )
  }

  const renderContent = () => {
    if (isLoading) {
      return (
        <LoadingContainer>
          <LoadingIndicator size={32} topPadding={0} />
        </LoadingContainer>
      )
    }

    if (appointments.length === 0) {
      return (
        <EmptyStateRow testID={tID('UpcomingSessionsModule-emptyState')}>
          <EmptyIconContainer>
            <BookSessionsIcon size={20} fillColor={colors.iconDefault} />
          </EmptyIconContainer>
          <BodyText
            text={formatMessage({
              defaultMessage: 'No upcoming sessions',
              description: 'Empty state message when no upcoming sessions are scheduled',
            })}
            size={BodyTextSize.DEFAULT}
            color={colors.textSecondary}
          />
          {onSchedulePress && (
            <EmptyStateAction>
              <Link
                text={formatMessage({
                  defaultMessage: 'Schedule',
                  description: 'Button to schedule a session from the upcoming sessions empty state',
                })}
                onPress={onSchedulePress}
                testID={tID('UpcomingSessionsModule-scheduleButton')}
              />
            </EmptyStateAction>
          )}
        </EmptyStateRow>
      )
    }

    return appointments.map(renderSessionRow)
  }

  return (
    <View testID={tID('UpcomingSessionsModule')}>
      <SessionsCard testID={tID('UpcomingSessionsModule-card')}>
        <ModuleHeader>
          <ModuleHeaderText>
            <Title
              text={
                <FormattedMessage
                  defaultMessage='Upcoming sessions'
                  description='Header for the upcoming sessions module'
                />
              }
              size={TitleSize.SMALL}
            />
            {subtitle && <BodyText text={subtitle} size={BodyTextSize.DEFAULT} color={colors.textSecondary} />}
          </ModuleHeaderText>
          <Link
            text={
              <BodyText
                text={formatMessage({ defaultMessage: 'View all', description: 'Link to view all upcoming sessions' })}
                size={BodyTextSize.DEFAULT}
                color={colors.textActive}
              />
            }
            onPress={onViewAllPress}
          />
        </ModuleHeader>
        <Divider />
        {renderContent()}
      </SessionsCard>
    </View>
  )
}
